// src/components/WheelCanvas.tsx
import { useEffect, useRef, useState } from 'react'
import { getWinnerIndex } from '../utils/wheelUtils'
import confetti from 'canvas-confetti'

type Props = {
  names: string[]
  isSpinning: boolean       // true while Space is held
  onStop: (winner: string) => void
  centerIcon: string
}

export default function WheelCanvas({ names, isSpinning, onStop, centerIcon }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const angleRef = useRef(0)            // current angle in radians
  const velocityRef = useRef(0)         // rad per ms
  const lastTimeRef = useRef<number | null>(null)
  const frameRef = useRef<number|undefined>(undefined)
  const [isInitialized, setIsInitialized] = useState(false)
  const hasDeclaredWinnerRef = useRef(false)
  const hasStartedSpinningRef = useRef(false)
  const winningIndexRef = useRef<number | null>(null) // Store the winning index for debugging
  const lastSpinningStateRef = useRef(false) // Track previous spinning state
  
  // tweak these to taste:
  const MAX_SPEED = 0.015      // ~0.015 rad/ms → ~0.85 rot/s (reduced for slower spin)
  const DECEL = 0.000005       // rad/ms² (reduced for longer spin-down)
  const RADIUS = 250
  
  // Animation configs
  const SPIN_UP_DURATION = 1000        // time in ms to reach full speed
  const SPIN_SOUND_ENABLED = true     // whether to play spinning sound
  const MIN_VELOCITY_FOR_WINNER = 0.0001 // Minimum velocity before declaring winner

  // keep latest onStop in a ref so our loop always calls the right one
  const onStopRef = useRef(onStop)
  useEffect(() => { onStopRef.current = onStop }, [onStop])

  // Reset winner declaration when names change
  useEffect(() => {
    hasDeclaredWinnerRef.current = false;
    hasStartedSpinningRef.current = false;
    winningIndexRef.current = null;
    
    // Initialize when names are first added
    if (names.length > 0 && !isInitialized) {
      setIsInitialized(true)
    }
  }, [names, isInitialized])

  // Create a spinning tick sound effect
  const playTickSound = () => {
    if (!SPIN_SOUND_ENABLED) return
    
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()
      const oscillator = audioContext.createOscillator()
      const gainNode = audioContext.createGain()
      
      oscillator.type = 'sine'
      oscillator.frequency.value = 800 + Math.random() * 200
      gainNode.gain.value = 0.05
      
      oscillator.connect(gainNode)
      gainNode.connect(audioContext.destination)
      
      oscillator.start()
      oscillator.stop(audioContext.currentTime + 0.03)
    } catch (e) {
      console.log('Audio not supported in this browser')
    }
  }

  // Helper function to get the correct winner based on pointer position
  const getCorrectWinnerIndex = (angle: number, totalSegments: number): number => {
    if (totalSegments === 0) return -1;
    
    // The pointer is at the top of the wheel (270 degrees or 3π/2 radians in the canvas coordinate system)
    // Each time the wheel rotates by angle, the segment at 270° is the one pointed at
    
    // Calculate the segment size in radians
    const arc = (2 * Math.PI) / totalSegments;
    
    // Determine which segment is at the top pointer position
    // First, normalize the angle to [0, 2π)
    const normalizedAngle = angle % (2 * Math.PI);
    
    // The formula below calculates which segment is at the top (3π/2) position:
    // 1. (3π/2 - normalizedAngle) calculates how far we need to go from 3π/2 to reach the current rotation angle
    // 2. % (2π) normalizes this value to [0, 2π)
    // 3. Dividing by arc and taking the floor gives us the segment index
    // 4. % totalSegments ensures the index is within range
    const topSegmentOffset = (3 * Math.PI / 2 - normalizedAngle) % (2 * Math.PI);
    let segmentIndex = Math.floor(topSegmentOffset / arc) % totalSegments;
    
    // Handle negative indices (can happen due to the subtraction)
    if (segmentIndex < 0) segmentIndex += totalSegments;
    
    return segmentIndex;
  };

  useEffect(() => {
    // Detect changes in spinning state
    // If we were previously not spinning and now we are, reset the state
    if (isSpinning && !lastSpinningStateRef.current) {
      hasStartedSpinningRef.current = true;
      hasDeclaredWinnerRef.current = false;
      winningIndexRef.current = null;
      // Start spinning with acceleration
      velocityRef.current = 0.001 // Start from very slow
      lastTimeRef.current = null
      console.log("Started spinning, reset winner state");
    }
    
    // Update the last spinning state for the next comparison
    lastSpinningStateRef.current = isSpinning;

    const render = (ts: number) => {
      if (lastTimeRef.current != null) {
        const dt = ts - lastTimeRef.current
        
        // Spin logic with animation
        if (isSpinning) {
          // Accelerate to full speed
          const elapsed = Date.now() - (lastTimeRef.current - dt)
          const progress = Math.min(1, elapsed / SPIN_UP_DURATION)
          velocityRef.current = MAX_SPEED * progress
          
          // Play tick sound on quarter rotations
          const prevAngle = angleRef.current
          const newAngle = (angleRef.current + velocityRef.current * dt) % (2 * Math.PI)
          const quarterTurns = Math.floor(newAngle / (Math.PI / 2))
          const prevQuarterTurns = Math.floor(prevAngle / (Math.PI / 2))
          
          if (quarterTurns !== prevQuarterTurns) {
            playTickSound()
          }
        } else if (hasStartedSpinningRef.current) {
          // Only decelerate if we've started spinning
          // Decelerate with improved physics - much slower deceleration
          velocityRef.current = Math.max(0, velocityRef.current - DECEL * dt * (0.9 + Math.random() * 0.2))
          
          // Add slight variation for realistic feel
          if (velocityRef.current < MAX_SPEED * 0.3 && velocityRef.current > 0) {
            // Add small random variations when slowing down
            velocityRef.current *= (1 - 0.005 * Math.random())
            
            // Occasional slight resistance point (like a bump)
            if (Math.random() < 0.01) {
              velocityRef.current *= 0.95
            }
          }
        }

        angleRef.current = (angleRef.current + velocityRef.current * dt) % (2 * Math.PI)
      }
      lastTimeRef.current = ts

      // draw frame
      const canvas = canvasRef.current
      if (canvas) {
        const ctx = canvas.getContext('2d')
        if (ctx) {
          // Calculate current winner for visualization
          if (names.length > 0) {
            winningIndexRef.current = getCorrectWinnerIndex(angleRef.current, names.length);
          }
          
          drawWheel(ctx, names, angleRef.current, centerIcon, velocityRef.current, isInitialized, winningIndexRef.current)
        }
      }

      // Has the wheel slowed down enough to declare a winner?
      const hasSlowedDown = velocityRef.current <= MIN_VELOCITY_FOR_WINNER;
      const shouldDeclareWinner = hasStartedSpinningRef.current && 
                                  !hasDeclaredWinnerRef.current && 
                                  !isSpinning && 
                                  hasSlowedDown && 
                                  names.length > 0;
      
      if (shouldDeclareWinner) {
        // Mark that we've declared a winner for this spin cycle
        hasDeclaredWinnerRef.current = true;
        
        // Use our corrected winner index function
        const idx = getCorrectWinnerIndex(angleRef.current, names.length);
        winningIndexRef.current = idx;
        
        // For debugging
        console.log('Final angle:', angleRef.current, 'Winner index:', idx, 'Winner name:', names[idx]);
        
        // Announce the winner
        onStopRef.current(names[idx]);
        
        // Launch more impressive confetti!
        const origin = { y: 0.6 };
        
        // Multiple bursts of confetti for dramatic effect
        confetti({ particleCount: 100, spread: 70, origin, colors: ['#ff0000', '#ff7700', '#ffff00'] });
        
        setTimeout(() => {
          confetti({ particleCount: 50, angle: 60, spread: 55, origin, colors: ['#00ff00', '#0000ff', '#ff00ff'] });
        }, 200);
        
        setTimeout(() => {
          confetti({ particleCount: 50, angle: 120, spread: 55, origin, colors: ['#ffffff', '#f5a623', '#2ecc71'] });
        }, 400);
      }

      // Continue animation frame
      frameRef.current = requestAnimationFrame(render);
    }

    frameRef.current = requestAnimationFrame(render)
    return () => { if (frameRef.current) cancelAnimationFrame(frameRef.current) }
  }, [names, centerIcon, isSpinning, isInitialized])

  return <canvas ref={canvasRef} width={RADIUS*2} height={RADIUS*2} className="wheel-canvas" />
}


// drawWheel with a little speed‑based "wobble"
function drawWheel(
  ctx: CanvasRenderingContext2D,
  names: string[],
  angle: number,
  centerIcon: string,
  velocity: number,
  showPointer: boolean,
  winningIndex: number | null
) {
  const total = names.length
  
  // Don't draw anything if there are no names
  if (total === 0) {
    ctx.clearRect(0, 0, 500, 500)
    ctx.fillStyle = '#f8f8f8'
    ctx.fillRect(0, 0, 500, 500)
    
    // Draw instruction text
    ctx.fillStyle = '#555'
    ctx.textAlign = 'center'
    ctx.font = '16px Arial'
    ctx.fillText('Enter names or upload a list to start', 250, 250)
    return
  }
  
  const arc = (2 * Math.PI) / total
  const R = 250

  ctx.clearRect(0, 0, 500, 500)
  
  // Draw background gradient for more visual appeal
  const gradient = ctx.createRadialGradient(R, R, 0, R, R, R)
  gradient.addColorStop(0, '#f8f8f8')
  gradient.addColorStop(1, '#e0e0e0')
  ctx.fillStyle = gradient
  ctx.fillRect(0, 0, 500, 500)
  
  ctx.save()
  ctx.translate(R, R)

  // Enhanced wobble effect based on velocity
  const wobbleFrequency = 10 + velocity * 500 // Higher frequency at higher speeds
  const wobbleAmount = Math.min(0.08, velocity * 15) // Limits maximum wobble
  const wobble = 1 + Math.sin(angle * wobbleFrequency) * wobbleAmount
  
  ctx.scale(wobble, wobble)
  ctx.rotate(angle)

  // Draw wheel segments with gradients and borders
  names.forEach((name, i) => {
    const start = i * arc
    const end = start + arc

    // Create gradient for each segment
    const segmentGradient = ctx.createRadialGradient(0, 0, R * 0.5, 0, 0, R)
    const hue = (i * 360) / total
    
    // Highlight the winning segment if applicable
    if (winningIndex === i) {
      segmentGradient.addColorStop(0, `hsl(${hue},95%,75%)`) // Brighter for the winning segment
      segmentGradient.addColorStop(1, `hsl(${hue},85%,65%)`)
    } else {
      segmentGradient.addColorStop(0, `hsl(${hue},90%,65%)`)
      segmentGradient.addColorStop(1, `hsl(${hue},80%,55%)`)
    }
    
    ctx.beginPath()
    ctx.moveTo(0, 0)
    ctx.arc(0, 0, R - 10, start, end)
    ctx.closePath()
    ctx.fillStyle = segmentGradient
    ctx.fill()
    
    // Add segment border
    ctx.strokeStyle = 'rgba(255,255,255,0.3)'
    ctx.lineWidth = 2
    ctx.stroke()

    // Text placement with shadow for better readability
    ctx.save()
    ctx.rotate(start + arc / 2)
    
    // Set text color based on whether this is the winning segment
    if (winningIndex === i) {
      ctx.fillStyle = '#ffffff'; // Brighter text for the winning segment
      ctx.shadowColor = 'rgba(0,0,0,0.7)';
      ctx.shadowBlur = 5;
    } else {
      ctx.fillStyle = '#fff'
      ctx.shadowColor = 'rgba(0,0,0,0.5)'
      ctx.shadowBlur = 3;
    }
    
    ctx.textAlign = 'right'
    ctx.font = winningIndex === i ? 'bold 18px Arial' : 'bold 16px Arial'
    
    ctx.shadowOffsetX = 1
    ctx.shadowOffsetY = 1
    
    // Trim and fit text if needed
    let displayName = name;
    if (ctx.measureText(name).width > R - 40) {
      // Truncate long names
      const maxLength = Math.floor(name.length * 0.8);
      displayName = name.substring(0, maxLength) + '...';
    }
    
    // Text with better positioning
    ctx.fillText(displayName, R - 20, 5)
    ctx.restore()
  })

  ctx.restore()

  // Draw center icon with shadow
  if (centerIcon) {
    const img = new Image()
    img.src = centerIcon
    img.onload = () => {
      ctx.save()
      
      // Add shadow to icon
      ctx.shadowColor = 'rgba(0,0,0,0.3)'
      ctx.shadowBlur = 5
      ctx.shadowOffsetX = 2
      ctx.shadowOffsetY = 2
      
      ctx.drawImage(img, R - 25, R - 25, 50, 50)
      ctx.restore()
    }
  }

  // Draw pointer only if wheel is initialized
  if (showPointer) {
    // Shadow and glow for the pointer
    ctx.save()
    ctx.shadowColor = 'rgba(0,0,0,0.5)'
    ctx.shadowBlur = 5
    ctx.shadowOffsetX = 2
    ctx.shadowOffsetY = 2
    
    // Draw a nicer pointer (triangle with curved base)
    const pointerHeight = 40;
    const pointerWidth = 20;
    const pointerBase = R - 10;
    
    // Animate pointer color based on velocity
    const pointerHue = (velocity * 5000) % 360;
    
    // Draw pointer with gradient
    const pointerGradient = ctx.createLinearGradient(R, R - pointerBase - pointerHeight, R, R - pointerBase);
    pointerGradient.addColorStop(0, `hsl(${pointerHue}, 100%, 50%)`);
    pointerGradient.addColorStop(1, `hsl(${pointerHue}, 100%, 40%)`);
    
    ctx.beginPath();
    ctx.moveTo(R, R - pointerBase - pointerHeight); // Tip of pointer
    ctx.lineTo(R + pointerWidth/2, R - pointerBase); // Right corner
    ctx.quadraticCurveTo(R, R - pointerBase + 5, R - pointerWidth/2, R - pointerBase); // Curved bottom
    ctx.closePath();
    
    ctx.fillStyle = pointerGradient;
    ctx.fill();
    
    // Add pointer border
    ctx.strokeStyle = 'rgba(255,255,255,0.7)';
    ctx.lineWidth = 2;
    ctx.stroke();
    
    // Add a small highlight effect
    ctx.beginPath();
    ctx.moveTo(R - pointerWidth/4, R - pointerBase - pointerHeight*0.7);
    ctx.lineTo(R, R - pointerBase - pointerHeight*0.9);
    ctx.lineTo(R + pointerWidth/4, R - pointerBase - pointerHeight*0.7);
    ctx.strokeStyle = 'rgba(255,255,255,0.7)';
    ctx.lineWidth = 1;
    ctx.stroke();
    
    // For debugging - Show the current winner name
    if (winningIndex !== null && winningIndex >= 0 && winningIndex < names.length) {
      // Draw a background for better visibility
      ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
      ctx.beginPath();
      ctx.roundRect(R - 75, R - pointerBase - pointerHeight - 30, 150, 20, 5);
      ctx.fill();
      
      // Draw text
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 14px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(`Pointing at: ${names[winningIndex]}`, R, R - pointerBase - pointerHeight - 15);
    }
    
    ctx.restore();
  }
}
